import React, { useEffect, useState } from "react";
import axios from "axios";
import { useGetUserID } from "../hooks/useGetUserID";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from '../config';
import '../styles/savedrecipes.css';
import '../styles/searchrecipes-modern.css';
import { useCookies } from 'react-cookie';
import RecipeImage from '../components/RecipeImage';
import HeartIcon from '../components/HeartIcon';
import LoadingSpinner from '../components/LoadingSpinner';
import { calculateRecipeNutrition, formatNutritionValue } from '../utils/nutritionCalculator';

export default function FavoriteRecipes() {
    const [cookies] = useCookies(["access_token"]);
    const [recipes, setRecipes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [costsCalculated, setCostsCalculated] = useState(false);
    const userID = useGetUserID();
    const navigate = useNavigate();

    // Token validation state
    const [tokenValidated, setTokenValidated] = useState(false);
    const [tokenValidationAttempted, setTokenValidationAttempted] = useState(false);
    
    // Nutrition tracking
    const [recipesNutrition, setRecipesNutrition] = useState({});
    
    // Filtering and sorting
    const [sortBy, setSortBy] = useState('default');
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [cookingTimeFilter, setCookingTimeFilter] = useState('any');
    const [difficultyFilter, setDifficultyFilter] = useState('any');
    const [preferSpecials, setPreferSpecials] = useState(false);
    const [preferCupboard, setPreferCupboard] = useState(false);
    const [nutritionFilter, setNutritionFilter] = useState('any');

    // Helper to get auth token
    const getAuthToken = () => cookies.access_token || window.localStorage.getItem('auth_token');

    // Helper to build Authorization header
    const getAuthHeaders = () => {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    // Helper to pre-fetch nutrition for recipes
    const preFetchNutrition = async (recipesList) => {
        if (!recipesList || recipesList.length === 0) return;
        
        const missingNutritionRecipes = recipesList.filter(r => !recipesNutrition[r._id]);
        if (missingNutritionRecipes.length === 0) return;

        const nutritionPromises = missingNutritionRecipes.map(async (recipe) => {
            try {
                const nutrition = await calculateRecipeNutrition(
                    recipe._id,
                    recipe.ingredients,
                    recipe.servings || 1
                );
                return { recipeId: recipe._id, nutrition };
            } catch (error) {
                console.error(`Error fetching nutrition for recipe ${recipe._id}:`, error);
                return null;
            }
        });

        const results = await Promise.all(nutritionPromises);
        const newNutrition = {};
        let hasNewData = false;
        
        results.forEach(r => {
            if (r) {
                newNutrition[r.recipeId] = r.nutrition;
                hasNewData = true;
            }
        });

        if (hasNewData) {
            setRecipesNutrition(prev => ({ ...prev, ...newNutrition }));
        }
    };

    // Sort recipes
    const sortRecipes = (recipesToSort) => {
        if (!recipesToSort || recipesToSort.length === 0) return recipesToSort;
        
        const sortedRecipes = [...recipesToSort];
        
        switch (sortBy) {
            case 'smart':
                return sortedRecipes.sort((a, b) => {
                    const aCupboardMatch = a.costCalculation?.ingredientsInCupboard || 0;
                    const bCupboardMatch = b.costCalculation?.ingredientsInCupboard || 0;
                    const aHasSpecials = a.costCalculation?.hasSpecials || false;
                    const bHasSpecials = b.costCalculation?.hasSpecials || false;
                    
                    if (preferCupboard && aCupboardMatch !== bCupboardMatch) {
                        return bCupboardMatch - aCupboardMatch;
                    }
                    
                    if (preferSpecials && aHasSpecials !== bHasSpecials) {
                        return bHasSpecials - aHasSpecials;
                    }
                    
                    const aCost = a.costCalculation?.totalCost || 0;
                    const bCost = b.costCalculation?.totalCost || 0;
                    const aServings = a.servings || 1;
                    const bServings = b.servings || 1;
                    const aCostPerServing = aCost / aServings;
                    const bCostPerServing = bCost / bServings;
                    
                    if (aCostPerServing !== bCostPerServing) {
                        return aCostPerServing - bCostPerServing;
                    }
                    
                    const aTime = a.cookingTime || 0;
                    const bTime = b.cookingTime || 0;
                    if (aTime !== bTime) {
                        return aTime - bTime;
                    }
                    
                    return (a.name || '').localeCompare(b.name || '');
                });
                
            case 'price-low':
                if (!costsCalculated) return sortedRecipes;
                return sortedRecipes.sort((a, b) => {
                    const priceA = a.costCalculation?.totalCost || 0;
                    const priceB = b.costCalculation?.totalCost || 0;
                    return priceA - priceB;
                });
                
            case 'price-high':
                if (!costsCalculated) return sortedRecipes;
                return sortedRecipes.sort((a, b) => {
                    const priceA = a.costCalculation?.totalCost || 0;
                    const priceB = b.costCalculation?.totalCost || 0;
                    return priceB - priceA;
                });
                
            default:
                return sortedRecipes;
        }
    };

    // Filter recipes
    const filterRecipes = (recipesToFilter) => {
        if (!recipesToFilter || recipesToFilter.length === 0) return recipesToFilter;
        
        return recipesToFilter.filter(recipe => {
            if (cookingTimeFilter !== 'any') {
                const cookingTime = recipe.cookingTime || 0;
                switch (cookingTimeFilter) {
                    case 'quick':
                        if (cookingTime > 30) return false;
                        break;
                    case 'medium':
                        if (cookingTime <= 30 || cookingTime > 90) return false;
                        break;
                    case 'long':
                        if (cookingTime <= 90) return false;
                        break;
                }
            }
            
            if (difficultyFilter !== 'any') {
                const cookingTime = recipe.cookingTime || 0;
                const ingredientCount = recipe.ingredients?.length || 0;
                const difficultyScore = (cookingTime / 60) + (ingredientCount / 10);
                
                switch (difficultyFilter) {
                    case 'easy':
                        if (difficultyScore > 2) return false;
                        break;
                    case 'medium':
                        if (difficultyScore <= 2 || difficultyScore > 4) return false;
                        break;
                    case 'hard':
                        if (difficultyScore <= 4) return false;
                        break;
                }
            }
            
            if (nutritionFilter !== 'any') {
                const nutrition = recipesNutrition[recipe._id];
                if (!nutrition || !nutrition.perServing) {
                    return false;
                }
                
                const calories = nutrition.perServing.Calories || 0;
                const protein = nutrition.perServing.Protein || 0;
                
                switch (nutritionFilter) {
                    case 'low-calorie':
                        if (calories >= 400) return false;
                        break;
                    case 'high-protein':
                        if (protein < 20) return false;
                        break;
                    case 'low-calorie-high-protein':
                        if (calories >= 400 || protein < 20) return false;
                        break;
                }
            }
            
            return true;
        });
    };

    // Token validation effect
    useEffect(() => {
        const validateToken = () => {
            const cookieToken = cookies.access_token;
            const localStorageToken = localStorage.getItem('auth_token');
            const localUserID = localStorage.getItem('userID');
            const hasToken = !!cookieToken || !!localStorageToken;

            console.log("SavedRecipes - Token validation:", {
                hasCookieToken: !!cookieToken,
                hasLocalStorageToken: !!localStorageToken,
                hasUserID: !!localUserID,
                userID: localUserID
            });

            if (!hasToken) {
                console.log("SavedRecipes - No token found, redirecting to auth");
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                navigate('/auth');
                return;
            }

            const token = cookieToken || localStorageToken;
            
            try {
                const parts = token.split('.');
                if (parts.length !== 3) {
                    throw new Error('Invalid token format');
                }
                
                const payload = JSON.parse(atob(parts[1]));
                const now = Math.floor(Date.now() / 1000);
                
                if (payload.exp && payload.exp < now) {
                    console.log("SavedRecipes - Token expired, redirecting to auth");
                    setTokenValidated(false);
                    setTokenValidationAttempted(true);
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('userID');
                    navigate('/auth');
                    return;
                }
                
                console.log("SavedRecipes - Token validated successfully");
                setTokenValidated(true);
                setTokenValidationAttempted(true);
                
            } catch (tokenError) {
                console.error('SavedRecipes - Token validation failed:', tokenError);
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                localStorage.removeItem('auth_token');
                localStorage.removeItem('userID');
                navigate('/auth');
            }
        };

        validateToken();
    }, [cookies.access_token, navigate]);

    // Listen for auth token updates from login
    useEffect(() => {
        const handleAuthTokenUpdate = (event) => {
            console.log("SavedRecipes - Auth token updated, re-validating");
            setTokenValidated(false);
            setTokenValidationAttempted(false);
        };

        window.addEventListener('auth-token-updated', handleAuthTokenUpdate);
        
        return () => {
            window.removeEventListener('auth-token-updated', handleAuthTokenUpdate);
        };
    }, []);

    useEffect(() => {
        const fetchFavoriteRecipes = async () => {
            console.log("SavedRecipes - fetchFavoriteRecipes called with:", {
                tokenValidationAttempted,
                tokenValidated,
                userID
            });

            // Wait for token validation to complete
            if (!tokenValidationAttempted) {
                console.log("SavedRecipes - Waiting for token validation...");
                return;
            }

            // If token is not valid, don't try to fetch
            if (!tokenValidated) {
                console.log("SavedRecipes - Token not validated, stopping");
                setIsLoading(false);
                return;
            }

            // If no userID, wait for it to be set
            if (!userID) {
                console.log("SavedRecipes - Waiting for userID...");
                // Give it a moment and try again
                const timeoutId = setTimeout(() => {
                    const storedUserID = localStorage.getItem('userID');
                    console.log("SavedRecipes - Rechecking userID from localStorage:", storedUserID);
                    if (!storedUserID) {
                        console.log("SavedRecipes - No userID found after timeout, redirecting to auth");
                        navigate('/auth');
                    }
                }, 1000);
                
                return () => clearTimeout(timeoutId);
            }

            try {
                setIsLoading(true);
                
                const url = `${API_BASE_URL}/recipes/savedRecipes/${userID}`;
                console.log("SavedRecipes - Fetching favorite recipes from:", url);
                
                const response = await axios.get(url, {
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json'
                    }
                });
                
                console.log("SavedRecipes - Server response:", response.data);
                
                if (response.data && response.data.savedRecipes) {
                    const savedRecipesList = response.data.savedRecipes;
                    setRecipes(savedRecipesList);
                    
                    // Pre-fetch nutrition data
                    await preFetchNutrition(savedRecipesList);
                } else {
                    console.log("SavedRecipes - No favorite recipes found");
                    setRecipes([]);
                }
            } catch (error) {
                console.error("SavedRecipes - Error fetching favorite recipes:", error);
                console.error("SavedRecipes - Error details:", error.response?.data);
                if (error.response?.status === 401) {
                    console.log("SavedRecipes - Unauthorized, redirecting to auth");
                    navigate('/auth');
                } else {
                    alert("Error fetching favorite recipes. Please try again.");
                    setRecipes([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchFavoriteRecipes();
    }, [userID, tokenValidated, tokenValidationAttempted, navigate]);

    // Reset price sort when costs are no longer calculated
    useEffect(() => {
        if (!costsCalculated && (sortBy === 'price-low' || sortBy === 'price-high')) {
            setSortBy('default');
        }
    }, [costsCalculated, sortBy]);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showFilterDropdown && !event.target.closest('.filter-dropdown') && !event.target.closest('.filter-main-btn') && !event.target.closest('.filter-dropdown-backdrop')) {
                setShowFilterDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilterDropdown]);

    // Prevent body scroll when filter is open on mobile
    useEffect(() => {
        if (showFilterDropdown) {
            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
                document.body.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
            }
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [showFilterDropdown]);

    const removeFavorite = async (recipeID) => {
        try {
            if (!userID || !getAuthToken()) {
                alert("Please log in to manage favorites");
                navigate('/auth');
                return;
            }

            await axios.put(`${API_BASE_URL}/recipes/unsave`, 
                { recipeID, userID },
                {
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            setRecipes(prevRecipes => prevRecipes.filter(recipe => recipe._id !== recipeID));
            alert("Recipe removed from favorites!");
        } catch (error) {
            console.error("Error removing from favorites:", error);
            const errorMessage = error.response?.data?.message || "Error removing from favorites";
            alert(errorMessage);
        }
    };

    // Modern Recipe Card Component
    const ModernRecipeCard = ({ recipe }) => {
        const nutritionData = recipesNutrition[recipe._id] || null;
        const nutritionReady = !!nutritionData;
        
        const getDietaryCategory = () => {
            if (recipe.isVegan) return 'Vegan';
            if (recipe.isVegetarian) return 'Vegetarian';
            if (recipe.categories && recipe.categories.length > 0) {
                return recipe.categories[0].charAt(0).toUpperCase() + recipe.categories[0].slice(1);
            }
            return 'Veggie';
        };

        const getDietaryClass = () => {
            if (recipe.isVegan) return 'dietary-vegan';
            if (recipe.isVegetarian) return 'dietary-vegetarian';
            return 'dietary-regular';
        };

        const getCostDisplay = () => {
            if (recipe.costCalculation && recipe.costCalculation.totalCost) {
                const servings = recipe.servings || 1;
                const costPerServing = recipe.costCalculation.totalCost / servings;
                return `R${costPerServing.toFixed(2)} per serving`;
            }
            return null;
        };

        const handleRecipeClick = () => {
            navigate(`/recipe/${recipe._id}`);
        };
        
        return (
            <div 
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer"
                onClick={handleRecipeClick}
            >
                {/* Compact Recipe Image */}
                <div className="relative">
                    <RecipeImage 
                        imageUrl={recipe.imageUrl} 
                        alt={recipe.name}
                        className="w-full h-32 object-cover"
                    />
                    
                    {/* Special Deals Badge */}
                    {recipe.costCalculation?.hasSpecials && (
                        <div className="absolute top-1.5 left-1.5">
                            <span className="bg-orange-500 text-white px-1.5 py-0.5 rounded-full text-xs font-semibold">
                                Special
                            </span>
                        </div>
                    )}
                    
                    {/* Heart Icon */}
                    <div className="absolute top-1.5 right-1.5">
                        <button 
                            className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                removeFavorite(recipe._id);
                            }}
                            aria-label="Remove from favorites"
                        >
                            <svg 
                                className="w-3.5 h-3.5 text-red-500" 
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                            >
                                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
                
                {/* Compact Recipe Content */}
                <div className="p-3">
                    <h3 className="font-semibold text-sm text-gray-900 mb-1 line-clamp-2 leading-tight">
                        {recipe.name}
                    </h3>
                    
                    {/* Compact Cook Time */}
                    <div className="flex items-center text-xs text-gray-600 mb-2">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {recipe.cookingTime} mins
                    </div>
                    
                    {/* Ingredient Count and Cupboard Match */}
                    <div className="mb-2">
                        <div className="flex items-center text-xs text-gray-600 mb-1">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            {recipe.ingredients?.length || 0} ingredients
                                            </div>
                        
                        {recipe.costCalculation && userID && recipe.costCalculation.ingredientsInCupboard > 0 && (
                            <div className="flex items-center text-xs text-green-600">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                {recipe.costCalculation.ingredientsInCupboard}/{recipe.costCalculation.totalIngredients} in cupboard
                                            </div>
                                        )}
                                    </div>
                    
                    {/* Compact Cost Display */}
                    {getCostDisplay() && (
                        <div className="mb-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-600">Cost:</span>
                                <span className="font-semibold text-green-600 text-sm">{getCostDisplay()}</span>
                            </div>
                            {recipe.costCalculation?.cupboardSavings > 0 && (
                                <div className="text-xs text-green-600">
                                    Save R{recipe.costCalculation.cupboardSavings.toFixed(2)} total
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Nutritional Information */}
                    {nutritionReady && nutritionData && nutritionData.perServing && (
                        <div className="mb-2 p-2 bg-gray-50 rounded text-xs">
                            <div className="font-semibold text-gray-700 mb-1">Nutrition per serving:</div>
                            <div className="grid grid-cols-2 gap-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Calories:</span>
                                    <span className="font-medium">{formatNutritionValue('Calories', nutritionData.perServing.Calories)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Protein:</span>
                                    <span className="font-medium">{formatNutritionValue('Protein', nutritionData.perServing.Protein)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Carbs:</span>
                                    <span className="font-medium">{formatNutritionValue('Carbs', nutritionData.perServing.Carbs)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Fats:</span>
                                    <span className="font-medium">{formatNutritionValue('Fats', nutritionData.perServing.Fats)}</span>
                                </div>
                            </div>
                </div>
                    )}
                    
                    {/* Compact Dietary Tag */}
                    <div className="flex justify-between items-center">
                        <span className={`${getDietaryClass()} px-2 py-0.5 rounded-full text-xs font-medium`}>
                            {getDietaryCategory()}
                        </span>
                        
                        {/* Author */}
                        <span className="text-xs text-gray-500">{recipe.createdBy || 'Mob'}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Show loading spinner
    if (isLoading) {
        return (
            <div className="full-page-loading">
                <LoadingSpinner size="xl" color="primary" text="Loading your favorite recipes..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Header Section */}
            <div className="bg-white py-6 border-b border-gray-100">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Favorite Recipes</h1>
                    <p className="text-gray-600">Your saved collection of delicious recipes</p>
                </div>
                        </div>
                        
            {/* Filter & Sort Section */}
            {recipes.length > 0 && (
                <div className="bg-white border-b border-gray-100 py-3">
                    <div className="px-3 max-w-7xl mx-auto">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <h3 className="text-sm font-medium text-gray-700">Filter & Sort</h3>
                                {(sortBy !== 'default' || cookingTimeFilter !== 'any' || difficultyFilter !== 'any' || preferSpecials || preferCupboard || nutritionFilter !== 'any') && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Active
                                    </span>
                                )}
                            </div>
                            
                            <div className="relative">
                                <button
                                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                    className="filter-main-btn"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                                    </svg>
                                    Filter
                                    <svg className={`w-4 h-4 ml-2 transition-transform duration-200 ${showFilterDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                
                                {/* Filter Dropdown */}
                                {showFilterDropdown && (
                                    <>
                                        <div 
                                            className="filter-dropdown-backdrop"
                                            onClick={() => setShowFilterDropdown(false)}
                                        ></div>
                                        <div className="filter-dropdown">
                                            <div className="filter-dropdown-content">
                                                <div className="filter-drag-handle"></div>
                                                
                                                <div className="filter-mobile-header">
                                                    <h3 className="filter-mobile-title">Filter & Sort</h3>
                                                    <button
                                                        onClick={() => setShowFilterDropdown(false)}
                                                        className="filter-close-button"
                                                        aria-label="Close filter"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                
                                                {/* Sort Options */}
                                                <div className="filter-section">
                                                    <h4 className="filter-section-title">Sort By</h4>
                                                    <div className="filter-options">
                                                        {[
                                                            { value: 'default', label: 'Default Order' },
                                                            { value: 'smart', label: 'Smart Sort' },
                                                            ...(costsCalculated ? [
                                                                { value: 'price-low', label: 'Price: Low to High' },
                                                                { value: 'price-high', label: 'Price: High to Low' }
                                                            ] : [])
                                                        ].map(option => (
                                                            <label key={option.value} className="filter-option">
                                                                <input
                                                                    type="radio"
                                                                    name="sortBy"
                                                                    value={option.value}
                                                                    checked={sortBy === option.value}
                                                                    onChange={(e) => setSortBy(e.target.value)}
                                                                    className="filter-radio"
                                                                />
                                                                <span className="filter-option-label">{option.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                {/* Nutrition Filter */}
                                                <div className="filter-section">
                                                    <h4 className="filter-section-title">Nutrition</h4>
                                                    <div className="filter-options">
                                                        {[
                                                            { value: 'any', label: 'Any Nutrition' },
                                                            { value: 'low-calorie', label: 'Low Calorie (<400 cal/serving)' },
                                                            { value: 'high-protein', label: 'High Protein (≥20g/serving)' },
                                                            { value: 'low-calorie-high-protein', label: 'Low Calorie & High Protein' }
                                                        ].map(option => (
                                                            <label key={option.value} className="filter-option">
                                                                <input
                                                                    type="radio"
                                                                    name="nutrition"
                                                                    value={option.value}
                                                                    checked={nutritionFilter === option.value}
                                                                    onChange={async (e) => {
                                                                        const newFilter = e.target.value;
                                                                        setNutritionFilter(newFilter);
                                                                        
                                                                        if (newFilter !== 'any') {
                                                                            const recipesNeedingNutrition = recipes.filter(r => !recipesNutrition[r._id]);
                                                                            
                                                                            if (recipesNeedingNutrition.length > 0) {
                                                                                const nutritionPromises = recipesNeedingNutrition.map(async (recipe) => {
                                                                                    try {
                                                                                        const nutrition = await calculateRecipeNutrition(
                                                                                            recipe._id,
                                                                                            recipe.ingredients,
                                                                                            recipe.servings || 1
                                                                                        );
                                                                                        return { recipeId: recipe._id, nutrition };
                                                                                    } catch (error) {
                                                                                        console.error(`Error fetching nutrition for recipe ${recipe._id}:`, error);
                                                                                        return null;
                                                                                    }
                                                                                });
                                                                                
                                                                                const nutritionResults = await Promise.all(nutritionPromises);
                                                                                const newNutritionData = {};
                                                                                nutritionResults.forEach(result => {
                                                                                    if (result) {
                                                                                        newNutritionData[result.recipeId] = result.nutrition;
                                                                                    }
                                                                                });
                                                                                
                                                                                setRecipesNutrition(prev => ({
                                                                                    ...prev,
                                                                                    ...newNutritionData
                                                                                }));
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="filter-radio"
                                                                />
                                                                <span className="filter-option-label">{option.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                {/* Quick Filters */}
                                                <div className="filter-section">
                                                    <h4 className="filter-section-title">Quick Filters</h4>
                                                    <div className="filter-options">
                                                        <label className="filter-option">
                                                            <input
                                                                type="checkbox"
                                                                checked={preferCupboard}
                                                                onChange={(e) => setPreferCupboard(e.target.checked)}
                                                                className="filter-checkbox"
                                                            />
                                                            <span className="filter-option-label">Prefer Cupboard Ingredients</span>
                                                        </label>
                                                        <label className="filter-option">
                                                            <input
                                                                type="checkbox"
                                                                checked={preferSpecials}
                                                                onChange={(e) => setPreferSpecials(e.target.checked)}
                                                                className="filter-checkbox"
                                                            />
                                                            <span className="filter-option-label">Prefer Special Deals</span>
                                                        </label>
                                                    </div>
                                                </div>
                                                
                                                {/* Time & Difficulty */}
                                                <div className="filter-section">
                                                    <h4 className="filter-section-title">Cooking Time</h4>
                                                    <div className="filter-options">
                                                        {[
                                                            { value: 'any', label: 'Any Time' },
                                                            { value: 'quick', label: 'Quick (≤30 min)' },
                                                            { value: 'medium', label: 'Medium (30-90 min)' },
                                                            { value: 'long', label: 'Long (>90 min)' }
                                                        ].map(option => (
                                                            <label key={option.value} className="filter-option">
                                                                <input
                                                                    type="radio"
                                                                    name="cookingTime"
                                                                    value={option.value}
                                                                    checked={cookingTimeFilter === option.value}
                                                                    onChange={(e) => setCookingTimeFilter(e.target.value)}
                                                                    className="filter-radio"
                                                                />
                                                                <span className="filter-option-label">{option.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                <div className="filter-section">
                                                    <h4 className="filter-section-title">Difficulty</h4>
                                                    <div className="filter-options">
                                                        {[
                                                            { value: 'any', label: 'Any Level' },
                                                            { value: 'easy', label: 'Easy' },
                                                            { value: 'medium', label: 'Medium' },
                                                            { value: 'hard', label: 'Hard' }
                                                        ].map(option => (
                                                            <label key={option.value} className="filter-option">
                                                                <input
                                                                    type="radio"
                                                                    name="difficulty"
                                                                    value={option.value}
                                                                    checked={difficultyFilter === option.value}
                                                                    onChange={(e) => setDifficultyFilter(e.target.value)}
                                                                    className="filter-radio"
                                                                />
                                                                <span className="filter-option-label">{option.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                {/* Actions */}
                                                <div className="filter-actions">
                                                    <button
                                                        onClick={() => {
                                                            setSortBy('default');
                                                            setCookingTimeFilter('any');
                                                            setDifficultyFilter('any');
                                                            setPreferSpecials(false);
                                                            setPreferCupboard(false);
                                                            setNutritionFilter('any');
                                                        }}
                                                        className="filter-reset-all-btn"
                                                    >
                                                        Reset All
                                                    </button>
                                                    <button
                                                        onClick={() => setShowFilterDropdown(false)}
                                                        className="filter-apply-btn"
                                                    >
                                                        Apply Filters
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    </div>
                )}
                
            {/* Recipes Section */}
            <div className="py-4 bg-white">
                <div className="px-3 max-w-7xl mx-auto">
                    {recipes.length === 0 ? (
                        <div className="text-center py-12">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No favorite recipes yet</h3>
                            <p className="mt-1 text-sm text-gray-500">Start exploring and save recipes you love!</p>
                            <div className="mt-6">
                                <button
                                    onClick={() => navigate('/searchrecipes')}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                >
                                    <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    Discover Recipes
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Calculate Costs Button */}
                            {userID && (
                                <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                                    <div className="flex flex-col gap-2">
                                        <div>
                                            <h3 className="font-semibold text-green-800 text-sm">Calculate Recipe Costs</h3>
                                            <p className="text-xs text-green-700">Get accurate pricing for all your favorite recipes</p>
                                        </div>
                    <button 
                                            onClick={async () => {
                                                setIsLoading(true);
                                                try {
                                                    const url = `${API_BASE_URL}/recipes/savedRecipes/${userID}?calculateCosts=true`;
                                                    const response = await axios.get(url, {
                                                        headers: {
                                                            ...getAuthHeaders(),
                                                            'Content-Type': 'application/json'
                                                        }
                                                    });
                                                    
                                                    if (response.data && response.data.savedRecipes) {
                                                        setRecipes(response.data.savedRecipes);
                                                        setCostsCalculated(true);
                                                    }
                                                } catch (error) {
                                                    console.error('Error calculating costs:', error);
                                                    alert('Error calculating recipe costs. Please try again.');
                                                } finally {
                                                    setIsLoading(false);
                                                }
                                            }}
                                            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors w-full"
                                        >
                                            Calculate All Costs
                    </button>
                                    </div>
                                </div>
                            )}

                            {/* Recipe Grid */}
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-2 xl:grid-cols-4 xl:gap-1.5 2xl:grid-cols-5 2xl:gap-1">
                                {sortRecipes(filterRecipes(recipes)).map((recipe) => (
                                    <ModernRecipeCard key={recipe._id} recipe={recipe} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Social Proof Section */}
            <div className="py-4 bg-gray-50">
                <div className="px-3 text-center max-w-7xl mx-auto">
                    <h2 className="font-semibold text-lg text-gray-900 mb-2">
                        Your Personal Recipe Collection
                    </h2>
                    <p className="text-sm text-gray-600">
                        Keep all your favorite recipes in one place and cook them anytime
                    </p>
                </div>
            </div>
        </div>
    );
}
